import React from 'react';
import { connect } from 'react-redux';
import { hashHistory } from 'react-router';
import {
  Grid, Box, Typography, Chip, Divider, Tooltip,
} from '@mui/material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { formatDateLocale, getOrgTier, reloadPage } from '../../lib/utils.es6';
import { getAllPartnersRequest } from '../Partners/actions.es6';
import { getOrgProductTemplatesRequest } from '../OrganizationProductTemplates/Redux/actions';
import { getOrgIppConfigsRequest } from '../OrganizationIppConfigs/Redux/actions';
import { getOrgClaimInitConfigsRequest } from '../OrganizationClaimInitiation/Redux/actions';
import { getOrgCustomerStatusConfigsRequest } from '../OrganizationCustomerStatus/Redux/actions';
import { getOrgClaimProcConfigsRequest } from '../OrganizationClaimProcessing/Redux/actions';
import ButtonInc from '../../shared-ui/ButtonInc';
import ModalInc from '../../shared-ui/ModalInc';
import SkeletonWrapper from '../NewProductList/components/SkeletonWrapper/SkeletonWrapper';
import AlertDialog from '../../components/AlertDialog/index.es6';
import {
  getOrganizationRequest,
  updateOrganizationRequest,
  resetUpdateOrganizationSuccess,
  resetUpdateOrganizationError,
} from './Redux/actions';
import PartnersTab from './tabs/PartnersTab';
import ProductTemplatesTab from './tabs/ProductTemplatesTab';
import EmailTemplatesTab from './tabs/EmailTemplatesTab';
import SmsTemplatesTab from './tabs/SmsTemplatesTab';
import IppConfigsTab from './tabs/IppConfigsTab';
import ClaimInitiationTab from './tabs/ClaimInitiationTab';
import CustomerStatusTab from './tabs/CustomerStatusTab';
import ClaimProcessingTab from './tabs/ClaimProcessingTab';
import WebhookTemplatesTab from './tabs/WebhookTemplatesTab';
import DocumentTemplatesTab from './tabs/DocumentTemplatesTab';

var TABS = [
  'Overview',
  'Partners',
  'Product Templates',
  'Email Templates',
  'SMS Templates',
  'IPP Configs',
  'Claim Initiation',
  'Customer Status',
  'Claim Processing',
  'Webhook Templates',
  'Document Templates',
];

class OrganizationDetail extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: 0,
      showEditDialog: false,
      editName: '',
      editActive: true,
      showUpdateSuccess: false,
      showUpdateError: false,
      guidCopied: false,
    };
    this.copyGuid = this.copyGuid.bind(this);
    this.openInWebclient = this.openInWebclient.bind(this);
  }

  copyGuid() {
    var org = this.props.organizationsData.current;
    if (!org || !org.guid) { return; }
    try {
      if (navigator && navigator.clipboard) {
        navigator.clipboard.writeText(org.guid);
      } else {
        var ta = document.createElement('textarea');
        ta.value = org.guid;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      this.setState({ guidCopied: true });
      var self = this;
      setTimeout(function() { self.setState({ guidCopied: false }); }, 1500);
    } catch (e) { /* clipboard not available */ }
  }

  openInWebclient() {
    // For now this simply navigates to the webclient home. When the
    // first partner under this org becomes the "preview partner", we can
    // set it via setCurrentPartner before navigating.
    hashHistory.push('/');
    reloadPage();
  }

  // Builds the setup-progress checklist for the Overview tab.
  // Each step is a small object with: label, hint, tabIndex (to jump to
  // when clicked), and `done` (boolean derived from current redux state).
  buildSetupSteps(org) {
    var partnersList = (this.props.partnersData && this.props.partnersData.partners) || [];
    var orgPartners = partnersList.filter(function(p) {
      return p && p.organization_guid === org.guid;
    });

    var productCount = (this.props.orgProductTemplatesData
      && this.props.orgProductTemplatesData.list
      && this.props.orgProductTemplatesData.list.length) || 0;
    var ippCount = (this.props.orgIppConfigsData
      && this.props.orgIppConfigsData.list
      && this.props.orgIppConfigsData.list.length) || 0;
    var claimInitCount = (this.props.orgClaimInitConfigsData
      && this.props.orgClaimInitConfigsData.list
      && this.props.orgClaimInitConfigsData.list.length) || 0;
    var custStatusCount = (this.props.orgCustomerStatusConfigsData
      && this.props.orgCustomerStatusConfigsData.list
      && this.props.orgCustomerStatusConfigsData.list.length) || 0;
    var claimProcCount = (this.props.orgClaimProcConfigsData
      && this.props.orgClaimProcConfigsData.list
      && this.props.orgClaimProcConfigsData.list.length) || 0;

    return [
      {
        key: 'org',
        label: 'Organization created',
        hint: org.name,
        tabIndex: 0,
        done: true,
      },
      {
        key: 'partners',
        label: 'At least one partner added',
        hint: orgPartners.length === 0
          ? 'No partners yet'
          : orgPartners.length + ' partner' + (orgPartners.length === 1 ? '' : 's'),
        tabIndex: 1,
        done: orgPartners.length >= 1,
      },
      {
        key: 'products',
        label: '2–3 product templates configured',
        hint: productCount === 0
          ? 'No product templates yet'
          : productCount + ' product template' + (productCount === 1 ? '' : 's'),
        tabIndex: 2,
        done: productCount >= 2,
      },
      {
        key: 'ipp',
        label: 'IPP config URL set up',
        hint: ippCount === 0
          ? 'No IPP config yet'
          : ippCount + ' IPP config' + (ippCount === 1 ? '' : 's'),
        tabIndex: 5,
        done: ippCount >= 1,
      },
      {
        key: 'claimInit',
        label: 'Claim initiation set up',
        hint: claimInitCount === 0
          ? 'No claim initiation config yet'
          : claimInitCount + ' config' + (claimInitCount === 1 ? '' : 's'),
        tabIndex: 6,
        done: claimInitCount >= 1,
      },
      {
        key: 'customerStatus',
        label: 'Customer status homepage set up',
        hint: custStatusCount === 0
          ? 'No customer status config yet'
          : custStatusCount + ' config' + (custStatusCount === 1 ? '' : 's'),
        tabIndex: 7,
        done: custStatusCount >= 1,
      },
      {
        key: 'policyProc',
        label: 'Policy processing set up',
        hint: 'Coming in a later phase',
        tabIndex: null,
        done: false,
        disabled: true,
      },
      {
        key: 'claimProc',
        label: 'Claim processing set up',
        hint: claimProcCount === 0
          ? 'No claim processing config yet'
          : claimProcCount + ' config' + (claimProcCount === 1 ? '' : 's'),
        tabIndex: 8,
        done: claimProcCount >= 1,
      },
    ];
  }

  renderSetupChecklist(org) {
    var steps = this.buildSetupSteps(org);
    var enabledSteps = steps.filter(function(s) { return !s.disabled; });
    var enabledDone = enabledSteps.filter(function(s) { return s.done; }).length;
    var pct = enabledSteps.length === 0
      ? 0
      : Math.round((enabledDone / enabledSteps.length) * 100);
    var self = this;

    return (
      <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ px: 3, py: 2, bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Typography sx={{
              fontWeight: 700, fontSize: 13, color: '#374151',
              letterSpacing: 0.5, textTransform: 'uppercase',
            }}>
              Setup Progress
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#6B7280', fontWeight: 600 }}>
              {enabledDone} / {enabledSteps.length} steps
            </Typography>
          </Box>
          {/* Progress bar */}
          <Box sx={{
            mt: 1.25, height: 6, bgcolor: '#E5E7EB', borderRadius: 3, overflow: 'hidden',
          }}>
            <Box sx={{
              height: '100%',
              width: pct + '%',
              bgcolor: pct === 100 ? '#10B981' : '#3B82F6',
              transition: 'width 0.3s ease',
            }} />
          </Box>
          <Typography sx={{ fontSize: 11, color: '#9CA3AF', mt: 0.75 }}>
            {pct === 100
              ? 'All required setup complete.'
              : 'Complete each step to finish onboarding this organization.'}
          </Typography>
        </Box>

        {/* Checklist rows */}
        <Box>
          {steps.map(function(step, idx) {
            var isLast = idx === steps.length - 1;
            var clickable = !step.disabled && step.tabIndex !== null && step.tabIndex !== undefined;
            return (
              <Box key={step.key}
                onClick={clickable
                  ? function() { self.setState({ activeTab: step.tabIndex }); }
                  : undefined}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  px: 3, py: 1.75,
                  borderBottom: isLast ? 'none' : '1px solid #F3F4F6',
                  cursor: clickable ? 'pointer' : 'default',
                  opacity: step.disabled ? 0.55 : 1,
                  '&:hover': clickable ? { bgcolor: '#F9FAFB' } : {},
                }}>
                {/* Status indicator */}
                <Box sx={{
                  width: 20, height: 20, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700,
                  bgcolor: step.done ? '#10B981' : (step.disabled ? '#F3F4F6' : '#FFFFFF'),
                  color: step.done ? '#FFFFFF' : (step.disabled ? '#9CA3AF' : '#9CA3AF'),
                  border: step.done ? 'none' : '1.5px solid ' + (step.disabled ? '#E5E7EB' : '#D1D5DB'),
                  flexShrink: 0,
                }}>
                  {step.done ? '✓' : ''}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{
                    fontSize: 13, fontWeight: 600,
                    color: step.done ? '#374151' : (step.disabled ? '#9CA3AF' : '#111827'),
                    textDecoration: step.done ? 'line-through' : 'none',
                    textDecorationColor: '#9CA3AF',
                  }}>
                    {step.label}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#9CA3AF', mt: 0.25 }}>
                    {step.hint}
                  </Typography>
                </Box>
                {clickable && (
                  <Box component="span" sx={{
                    fontSize: 13, color: step.done ? '#10B981' : '#3B82F6',
                    fontWeight: 600,
                  }}>
                    {step.done ? 'View' : 'Set up →'}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  componentWillMount() {
    var guid = this.props.params && this.props.params.guid;
    if (guid) {
      this.props.dispatch(getOrganizationRequest(guid));
      // Stats bar + setup checklist need data from every config surface.
      this.props.dispatch(getAllPartnersRequest());
      this.props.dispatch(getOrgProductTemplatesRequest(guid));
      this.props.dispatch(getOrgIppConfigsRequest(guid));
      this.props.dispatch(getOrgClaimInitConfigsRequest(guid));
      this.props.dispatch(getOrgCustomerStatusConfigsRequest(guid));
      this.props.dispatch(getOrgClaimProcConfigsRequest(guid));
    }
  }

  componentDidUpdate(prevProps) {
    var data = this.props.organizationsData;
    var prev = prevProps.organizationsData;

    if (data.updated && !prev.updated) {
      this.setState({ showEditDialog: false, showUpdateSuccess: true });
    }
    if (data.updateError && !prev.updateError) {
      this.setState({ showUpdateError: true });
    }
  }

  openEdit() {
    var org = this.props.organizationsData.current;
    if (!org) return;
    this.setState({
      showEditDialog: true,
      editName: org.name,
      editActive: org.active !== false,
    });
  }

  handleUpdate() {
    var org = this.props.organizationsData.current;
    if (!org) return;
    this.props.dispatch(updateOrganizationRequest({
      guid: org.guid,
      name: this.state.editName,
      active: this.state.editActive,
    }));
  }

  render() {
    var data = this.props.organizationsData;
    var state = this.state;

    if (data.currentLoading) {
      return <SkeletonWrapper loading={true} />;
    }

    if (data.currentError) {
      return (
        <div style={{ padding: 20 }}>
          <ButtonInc onClick={() => hashHistory.push('/admin/organizations')}>
            ← Back
          </ButtonInc>
          <p style={{ color: 'red', marginTop: 16 }}>
            Failed to load organization: {String(data.currentError)}
          </p>
        </div>
      );
    }

    var org = data.current;
    if (!org) {
      return <SkeletonWrapper loading={true} />;
    }

    var tier = getOrgTier(org);
    var isComplex = tier === 'Complex';
    var initials = (function(name) {
      if (!name) { return '??'; }
      var parts = name.trim().split(/\s+/).slice(0, 3);
      return parts.map(function(p) { return p.charAt(0).toUpperCase(); }).join('');
    })(org.name);

    // Stat values — Partners count comes from state.partners if loaded.
    // Products count is org product templates list. Active policies is a
    // placeholder until the backend exposes the aggregate.
    var partnersList = (this.props.partnersData && this.props.partnersData.partners) || [];
    var partnersCount = partnersList.filter(function(p) {
      return p && p.organization_guid === org.guid;
    }).length;
    var productsCount = (this.props.orgProductTemplatesData
      && this.props.orgProductTemplatesData.list
      && this.props.orgProductTemplatesData.list.length) || 0;

    return (
      <Box sx={{ p: 3, bgcolor: '#FFFFFF', minHeight: '100vh' }}>
        {/* Back link */}
        <Box
          onClick={function() { hashHistory.push('/admin/organizations'); }}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            color: '#6B7280', fontSize: 13, cursor: 'pointer', mb: 2,
            '&:hover': { color: '#374151' },
          }}>
          ← All organizations
        </Box>

        {/* Header */}
        <Grid container alignItems="flex-start" spacing={2}
          sx={{ mb: 3, pb: 3, borderBottom: '1px solid #E5E7EB' }}>
          <Grid item>
            <Box sx={{
              width: 56, height: 56, borderRadius: 1.5,
              bgcolor: '#0B6E4F', color: '#FFFFFF',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 700, letterSpacing: 0.5,
            }}>
              {initials}
            </Box>
          </Grid>
          <Grid item xs>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, fontSize: 22, color: '#111827' }}>
                {org.name}
              </Typography>
              <Chip
                label={isComplex ? 'Complex Aspin' : 'Simple Aspin'}
                size="small"
                sx={{
                  height: 22, fontSize: 11, fontWeight: 600,
                  bgcolor: isComplex ? '#FEF3C7' : '#DCFCE7',
                  color: isComplex ? '#92400E' : '#166534',
                  border: '1px solid ' + (isComplex ? '#FDE68A' : '#BBF7D0'),
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
              <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                {org.active !== false ? (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <Box component="span" sx={{
                      width: 6, height: 6, borderRadius: '50%', bgcolor: '#10B981',
                      display: 'inline-block',
                    }} />
                    Active
                  </Box>
                ) : 'Inactive'}
              </Typography>
              <Box component="span" sx={{ color: '#D1D5DB', fontSize: 12 }}>·</Box>
              <Tooltip title={this.state.guidCopied ? 'Copied!' : 'Copy GUID'}>
                <Box onClick={this.copyGuid}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    fontSize: 12, fontFamily: 'monospace', color: '#6B7280',
                    cursor: 'pointer',
                    '&:hover': { color: '#374151' },
                  }}>
                  {org.code}
                  <Box component="span" sx={{ fontSize: 11 }}>
                    {this.state.guidCopied ? '✓' : '⧉'}
                  </Box>
                </Box>
              </Tooltip>
            </Box>
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {/* <ButtonInc btnType="secondary" onClick={this.openInWebclient}>
                ↗ Open in webclient
              </ButtonInc> */}
              <ButtonInc variant="contained" color="primary"
                onClick={this.openEdit.bind(this)}>
                ⚙ Org settings
              </ButtonInc>
            </Box>
          </Grid>
        </Grid>

        {/* Stats bar */}
        <Grid container spacing={4} sx={{ mb: 3, pb: 3, borderBottom: '1px solid #E5E7EB' }}>
          <Grid item>
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#9CA3AF',
              letterSpacing: 0.8, textTransform: 'uppercase' }}>
              Partners
            </Typography>
            <Typography sx={{ fontSize: 26, fontWeight: 700, color: '#111827', mt: 0.5 }}>
              {partnersCount}
            </Typography>
          </Grid>
          <Grid item>
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#9CA3AF',
              letterSpacing: 0.8, textTransform: 'uppercase' }}>
              Products
            </Typography>
            <Typography sx={{ fontSize: 26, fontWeight: 700, color: '#111827', mt: 0.5 }}>
              {productsCount}
            </Typography>
          </Grid>
          <Grid item>
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#9CA3AF',
              letterSpacing: 0.8, textTransform: 'uppercase' }}>
              Active Policies
            </Typography>
            <Typography sx={{ fontSize: 26, fontWeight: 700, color: '#D1D5DB', mt: 0.5 }}>
              —
            </Typography>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Tabs
          value={state.activeTab}
          onChange={(e, val) => this.setState({ activeTab: val })}
          variant="scrollable"
          scrollButtons="auto"
          style={{ borderBottom: '1px solid #e0e0e0', marginBottom: 16 }}
        >
          {TABS.map(function(label, i) {
            return <Tab key={i} label={label} />;
          })}
        </Tabs>

        {/* Tab panels */}
        {state.activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                {/* Card header */}
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    bgcolor: '#F9FAFB',
                    borderBottom: '1px solid #E5E7EB',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, fontSize: 13, color: '#374151', letterSpacing: 0.5, textTransform: 'uppercase' }}
                  >
                    Organization Details
                  </Typography>
                </Box>

                {/* Rows */}
                {[
                  {
                    label: 'Name',
                    value: (
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827', fontSize: 14 }}>
                        {org.name}
                      </Typography>
                    ),
                  },
                  {
                    label: 'Code',
                    value: (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          fontFamily: 'monospace',
                          fontSize: 13,
                          bgcolor: '#F3F4F6',
                          color: '#374151',
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          border: '1px solid #E5E7EB',
                        }}
                      >
                        {org.code}
                      </Box>
                    ),
                  },
                  {
                    label: 'Status',
                    value: (
                      <Chip
                        label={org.active !== false ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: 12,
                          fontWeight: 600,
                          bgcolor: org.active !== false ? '#F0FDF4' : '#FEF2F2',
                          color: org.active !== false ? '#15803D' : '#B91C1C',
                          border: org.active !== false ? '1px solid #BBF7D0' : '1px solid #FECACA',
                        }}
                      />
                    ),
                  },
                  {
                    label: 'Created',
                    value: (
                      <Typography variant="body2" sx={{ color: '#6B7280', fontSize: 13 }}>
                        {org.created_at ? formatDateLocale(org.created_at) : '—'}
                      </Typography>
                    ),
                  },
                  {
                    label: 'GUID',
                    value: (
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: 12,
                          color: '#6B7280',
                          wordBreak: 'break-all',
                        }}
                      >
                        {org.guid}
                      </Typography>
                    ),
                  },
                ].map(function(row, idx, arr) {
                  return (
                    <Box key={row.label}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          px: 3,
                          py: 1.75,
                          gap: 2,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            minWidth: 80,
                            color: '#9CA3AF',
                            fontSize: 12,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.4,
                            pt: 0.25,
                          }}
                        >
                          {row.label}
                        </Typography>
                        <Box sx={{ flex: 1 }}>{row.value}</Box>
                      </Box>
                      {idx < arr.length - 1 && (
                        <Divider sx={{ borderColor: '#F3F4F6' }} />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Grid>

            {/* Setup progress checklist */}
            <Grid item xs={12} md={6}>
              {this.renderSetupChecklist(org)}
            </Grid>
          </Grid>
        )}
        {state.activeTab === 1 && (
          <PartnersTab organizationGuid={org.guid} />
        )}
        {state.activeTab === 2 && (
          <ProductTemplatesTab organizationGuid={org.guid} />
        )}
        {state.activeTab === 3 && (
          <EmailTemplatesTab organizationGuid={org.guid} />
        )}
        {state.activeTab === 4 && (
          <SmsTemplatesTab organizationGuid={org.guid} />
        )}
        {state.activeTab === 5 && (
          <IppConfigsTab organizationGuid={org.guid} />
        )}
        {state.activeTab === 6 && (
          <ClaimInitiationTab organizationGuid={org.guid} />
        )}
        {state.activeTab === 7 && (
          <CustomerStatusTab organizationGuid={org.guid} />
        )}
        {state.activeTab === 8 && (
          <ClaimProcessingTab organizationGuid={org.guid} />
        )}
        {state.activeTab === 9 && (
          <WebhookTemplatesTab organizationGuid={org.guid} />
        )}
        {state.activeTab === 10 && (
          <DocumentTemplatesTab organizationGuid={org.guid} />
        )}

        {/* Edit dialog */}
        <ModalInc
          modalTitle="Edit Organization"
          open={state.showEditDialog}
          close={() => this.setState({ showEditDialog: false })}
          fullWidth
          maxWidth="sm"
          modalButtons={[
            <ButtonInc
              key="cancel"
              btnType="secondary"
              onClick={() => this.setState({ showEditDialog: false })}
            >
              Cancel
            </ButtonInc>,
            <ButtonInc
              key="save"
              variant="contained"
              color="primary"
              loading={data.updating}
              onClick={this.handleUpdate.bind(this)}
            >
              Save
            </ButtonInc>,
          ]}
        >
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12}>
              <label style={{ fontWeight: 600 }}>Name</label>
              <input
                value={state.editName}
                onChange={(e) => this.setState({ editName: e.target.value })}
                style={{
                  display: 'block', width: '100%', padding: '8px',
                  marginTop: 4, boxSizing: 'border-box',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <label style={{ fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={state.editActive}
                  onChange={(e) => this.setState({ editActive: e.target.checked })}
                  style={{ marginRight: 8 }}
                />
                Active
              </label>
            </Grid>
          </Grid>
        </ModalInc>

        {/* Update success */}
        {state.showUpdateSuccess && (
          <AlertDialog
            show={state.showUpdateSuccess}
            title="Updated"
            type="success"
            showConfirm
            confirmBtnText="OK"
            onConfirm={() => {
              this.props.dispatch(resetUpdateOrganizationSuccess());
              this.setState({ showUpdateSuccess: false });
            }}
          >
            Organization updated successfully.
          </AlertDialog>
        )}

        {/* Update error */}
        {state.showUpdateError && (
          <AlertDialog
            show={state.showUpdateError}
            title="Error"
            type="danger"
            showConfirm
            confirmBtnText="OK"
            onConfirm={() => {
              this.props.dispatch(resetUpdateOrganizationError());
              this.setState({ showUpdateError: false });
            }}
          >
            {data.updateError
              ? (data.updateError.message || JSON.stringify(data.updateError))
              : 'An error occurred.'}
          </AlertDialog>
        )}
      </Box>
    );
  }
}

export default connect(function(state) {
  return {
    organizationsData: state.organizationsData,
    partnersData: state.partners,
    orgProductTemplatesData: state.orgProductTemplatesData,
    orgIppConfigsData: state.orgIppConfigsData,
    orgClaimInitConfigsData: state.orgClaimInitConfigsData,
    orgCustomerStatusConfigsData: state.orgCustomerStatusConfigsData,
    orgClaimProcConfigsData: state.orgClaimProcConfigsData,
  };
})(OrganizationDetail);
